import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CalendarCheck, MessageSquare, Loader2, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { bookingApi, type BookingOrgProfile, type EstimateResponse } from '../../lib/bookingApi';

// ── Types ────────────────────────────────────────────────────────────────────

interface LocationState {
  estimate: EstimateResponse;
  bookingToken: string;
  orgProfile: BookingOrgProfile;
}

// ── Stripe setup ─────────────────────────────────────────────────────────────

const STRIPE_PK_TEST = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST as string | undefined;
const STRIPE_PK_LIVE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE as string | undefined;

const stripePromiseCache: Record<string, ReturnType<typeof loadStripe>> = {};
function getStripePromise(connectAccountId: string) {
  // Use test key by default, fallback to live
  const key = STRIPE_PK_TEST || STRIPE_PK_LIVE;
  if (!key) return null;
  const cacheKey = `${key}:${connectAccountId}`;
  if (!stripePromiseCache[cacheKey]) {
    stripePromiseCache[cacheKey] = loadStripe(key, { stripeAccount: connectAccountId });
  }
  return stripePromiseCache[cacheKey];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ── Deposit Payment Form (inside Stripe Elements) ───────────────────────────

function DepositPaymentForm({
  amountCents,
  orgName,
  brandPrimary,
  brandText,
  onSuccess,
}: {
  amountCents: number;
  orgName: string;
  brandPrimary: string;
  brandText: string;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message ?? 'Payment failed');
      setProcessing(false);
      return;
    }

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message ?? 'Payment failed');
      setProcessing(false);
    } else {
      setSucceeded(true);
      setProcessing(false);
      onSuccess();
    }
  }

  if (succeeded) {
    return (
      <div className="text-center py-4">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <CheckCircle size={28} className="text-green-500" />
        </div>
        <h3 className="text-lg font-bold text-midnight mb-1">Deposit Received!</h3>
        <p className="text-sm text-slate">
          Your moving date has been locked in with {orgName}. You&apos;ll receive a confirmation email soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-plum/10 flex items-center justify-center shrink-0">
          <CreditCard size={20} className="text-plum" />
        </div>
        <div>
          <h3 className="font-semibold text-midnight">Refundable Deposit</h3>
          <p className="text-sm text-slate">
            {formatCurrency(amountCents / 100)} to secure your moving date
          </p>
        </div>
      </div>

      <PaymentElement />

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full h-12 rounded-md font-semibold text-body transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] flex items-center justify-center gap-2"
        style={{ backgroundColor: brandPrimary, color: brandText }}
      >
        {processing ? (
          <><Loader2 size={18} className="animate-spin" /> Processing...</>
        ) : (
          `Pay ${formatCurrency(amountCents / 100)} Deposit`
        )}
      </button>
    </form>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function BookingEstimatePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showDepositCard, setShowDepositCard] = useState(false);
  const [depositClientSecret, setDepositClientSecret] = useState<string | null>(null);
  const [depositAmountCents, setDepositAmountCents] = useState<number>(0);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [depositPaid, setDepositPaid] = useState(false);

  // No state = direct navigation / invalid
  if (!state?.estimate || !state?.bookingToken || !state?.orgProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist px-4">
        <div className="bg-white rounded-lg shadow-brand-md p-8 text-center max-w-sm">
          <h1 className="text-xl font-semibold text-midnight mb-2">Session Expired</h1>
          <p className="text-slate text-body mb-4">
            Please go back and fill out the booking form again.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="text-plum font-medium hover:underline"
          >
            Return to booking form
          </button>
        </div>
      </div>
    );
  }

  const { estimate, bookingToken, orgProfile } = state;
  const hasRange = estimate.rangeLow != null && estimate.rangeHigh != null;
  const showLockInDate = orgProfile.bookingConfig?.enableLockInDate !== false;
  const showRequestEstimate = orgProfile.bookingConfig?.enableRequestEstimate !== false;
  const brandPrimary = orgProfile.bookingConfig?.brandPrimaryColor ?? '#6B2D8B';
  const brandText = orgProfile.bookingConfig?.brandTitleTextColor ?? '#FFFFFF';
  const connectAccountId = orgProfile.stripeConnectAccountId;
  const depositConfigured = !!(orgProfile.bookingConfig?.depositAmount && orgProfile.bookingConfig.depositAmount > 0);

  async function handleLockInDate() {
    setActionLoading('lock_in_date');
    setDepositError(null);

    // Record the action (best-effort)
    try {
      await bookingApi.recordAction(slug!, bookingToken, 'lock_in_date');
    } catch {
      // best-effort
    }

    // If Stripe Connect is active and deposit is configured, create a deposit PaymentIntent
    if (connectAccountId && depositConfigured) {
      try {
        const res = await bookingApi.createDeposit(slug!, bookingToken);
        setDepositClientSecret(res.data.clientSecret);
        setDepositAmountCents(res.data.amountCents);
        setShowDepositCard(true);
      } catch (err) {
        setDepositError((err as Error).message || 'Could not initiate deposit payment.');
        // Fall back to the deposit page
        navigate(`/${slug}/deposit`, {
          state: { estimate, bookingToken, orgProfile },
        });
      }
    } else {
      // No Stripe — navigate to the deposit info page
      navigate(`/${slug}/deposit`, {
        state: { estimate, bookingToken, orgProfile },
      });
    }

    setActionLoading(null);
  }

  async function handleRequestEstimate() {
    setActionLoading('request_estimate');
    try {
      await bookingApi.recordAction(slug!, bookingToken, 'request_estimate');
      navigate(`/${slug}/confirmation`, {
        state: { bookingToken, orgProfile },
      });
    } catch {
      navigate(`/${slug}/confirmation`, {
        state: { bookingToken, orgProfile },
      });
    } finally {
      setActionLoading(null);
    }
  }

  const stripePromise = connectAccountId ? getStripePromise(connectAccountId) : null;

  return (
    <div className="min-h-screen bg-mist">
      {/* Header */}
      <header
        className="border-b border-stone px-4 py-4"
        style={{ backgroundColor: brandPrimary, color: brandText }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft size={20} style={{ color: brandText }} />
          </button>
          {orgProfile.logoBase64 ? (
            <img
              src={orgProfile.logoBase64}
              alt={orgProfile.name}
              className="w-8 h-8 rounded-md object-contain shrink-0"
            />
          ) : (
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center font-bold shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: brandText }}
            >
              {orgProfile.name.charAt(0)}
            </div>
          )}
          <h1 className="text-lg font-bold leading-tight truncate" style={{ color: brandText }}>{orgProfile.name}</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Estimate Card */}
        <section className="bg-white rounded-lg shadow-brand-md p-6 text-center">
          <p className="text-sm text-slate mb-2">Your Estimated Moving Cost</p>
          {estimate.estimatedValue != null ? (
            <>
              <div className="text-3xl font-bold text-midnight mb-1">
                {hasRange
                  ? `${formatCurrency(estimate.rangeLow!)} – ${formatCurrency(estimate.rangeHigh!)}`
                  : `~${formatCurrency(estimate.estimatedValue)}`}
              </div>
              {hasRange && (
                <p className="text-xs text-slate">
                  Based on the details you provided (±{estimate.rangePercent}%)
                </p>
              )}
              {!hasRange && (
                <p className="text-xs text-slate">
                  Based on the details you provided
                </p>
              )}
            </>
          ) : (
            <div className="py-4">
              <p className="text-lg font-semibold text-midnight mb-1">
                We need a bit more info
              </p>
              <p className="text-sm text-slate">
                {estimate.reason === 'missing_addresses' && 'Please provide both origin and destination addresses.'}
                {estimate.reason === 'missing_bedrooms' && 'Please provide the number of bedrooms.'}
                {estimate.reason === 'missing_sqft' && 'Please provide the approximate square footage.'}
                {estimate.reason === 'missing_employee_count' && 'Please provide the number of employees.'}
                {!estimate.reason && 'Please complete the booking form to get an estimate.'}
              </p>
            </div>
          )}
        </section>

        {/* Inline Deposit Payment Card */}
        {showDepositCard && depositClientSecret && stripePromise && !depositPaid && (
          <section className="bg-white rounded-lg shadow-brand-md p-6">
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: depositClientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <DepositPaymentForm
                amountCents={depositAmountCents}
                orgName={orgProfile.name}
                brandPrimary={brandPrimary}
                brandText={brandText}
                onSuccess={() => setDepositPaid(true)}
              />
            </Elements>
          </section>
        )}

        {/* Deposit success card */}
        {depositPaid && (
          <section className="bg-white rounded-lg shadow-brand-md p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={28} className="text-green-500" />
            </div>
            <h3 className="text-lg font-bold text-midnight mb-1">Deposit Received!</h3>
            <p className="text-sm text-slate">
              Your moving date has been locked in with {orgProfile.name}. You&apos;ll receive a confirmation email soon.
            </p>
          </section>
        )}

        {depositError && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{depositError}</span>
          </div>
        )}

        {/* Action Buttons (hidden when deposit card is shown) */}
        {!showDepositCard && !depositPaid && (
          <section className="space-y-3">
            {showLockInDate && (
              <button
                type="button"
                disabled={actionLoading !== null}
                onClick={handleLockInDate}
                className="w-full rounded-lg p-4 text-left active:scale-[0.99] transition-all disabled:opacity-60 flex items-start gap-4"
                style={{ backgroundColor: brandPrimary, color: brandText }}
              >
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0 mt-0.5">
                  {actionLoading === 'lock_in_date' ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <CalendarCheck size={20} />
                  )}
                </div>
                <div>
                  <div className="font-semibold text-body">Lock In Moving Date</div>
                  <div className="text-sm mt-0.5" style={{ opacity: 0.8 }}>Requires a refundable deposit</div>
                </div>
              </button>
            )}

            {showRequestEstimate && (
              <button
                type="button"
                disabled={actionLoading !== null}
                onClick={handleRequestEstimate}
                className={`w-full rounded-lg p-4 text-left active:scale-[0.99] transition-all disabled:opacity-60 flex items-start gap-4 ${
                  showLockInDate
                    ? 'bg-white border-2 hover:bg-plum/5'
                    : ''
                }`}
                style={showLockInDate
                  ? { borderColor: brandPrimary, color: brandPrimary }
                  : { backgroundColor: brandPrimary, color: brandText }
                }
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={showLockInDate
                    ? { backgroundColor: `${brandPrimary}15` }
                    : { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }
                >
                  {actionLoading === 'request_estimate' ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <MessageSquare size={20} />
                  )}
                </div>
                <div>
                  <div className="font-semibold text-body">Request a Free Estimate</div>
                  <div className={`text-sm mt-0.5 ${showLockInDate ? 'text-slate' : ''}`} style={showLockInDate ? {} : { opacity: 0.8 }}>
                    Someone from {orgProfile.name} will reach out to you soon
                  </div>
                </div>
              </button>
            )}
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6">
        <span className="text-xs text-slate">Powered by <span className="font-semibold">IOMI Pro</span></span>
      </div>
    </div>
  );
}
